'use client';

import { create } from 'zustand';
import { LAST_LOCATION_MODE, LAST_LOCATION_STORAGE } from '~/features/activities/lastLocation';
import type { WidgetLocation, WidgetMode } from './types';

/**
 * Shared client store for the PDP location widget's delivery mode + location, so
 * the left-column widget and the right-panel location dropdown stay in sync.
 * Uses zustand (the app's store convention, see `features/activities/search/*`);
 * mutations go through the exported helpers so the side effects (persisting the
 * last mode/location) live in one place.
 */
type LocationWidgetStore = {
    mode: WidgetMode;
    location: WidgetLocation | null;
    /** The fixed partner venue chosen in `fixed` mode, or null when none picked yet. */
    fixedAddressId: string | null;
    /**
     * The activity this state was seeded for. The store is module-global, so on a
     * client-side nav from one PDP to another it must be re-seeded: venue ids belong to a
     * single activity, and carrying one into the next page makes the add-to-cart mutation
     * reject it server-side (the router verifies the activity-address link).
     */
    seededActivityId: number | null;
};

export const useLocationWidgetStore = create<LocationWidgetStore>(() => ({
    mode: 'fixed',
    location: null,
    fixedAddressId: null,
    seededActivityId: null,
}));

export function setWidgetMode(mode: WidgetMode) {
    useLocationWidgetStore.setState({ mode });
    // Persist the choice so opening an alternative's page in a new tab (a full page load,
    // which resets this store) re-seeds the same tab.
    if (typeof window !== 'undefined') LAST_LOCATION_MODE.setValue(mode);
}

/** Remember which fixed partner venue is selected (drives `fixed` mode). */
export function setWidgetFixedAddress(fixedAddressId: string | null) {
    useLocationWidgetStore.setState({ fixedAddressId, mode: 'fixed' });
    if (typeof window !== 'undefined') LAST_LOCATION_MODE.setValue('fixed');
}

export function setWidgetLocation(location: WidgetLocation | null) {
    useLocationWidgetStore.setState({ location });
    if (typeof window === 'undefined') return;
    if (!location) {
        // Clearing must also forget the stored location, otherwise the next page load
        // re-seeds the input with the city the user just removed.
        LAST_LOCATION_STORAGE.setValue(null);
        return;
    }
    // Preserve the radius the user already chose (filter / checkout) — only the point moved.
    const previousRadius = LAST_LOCATION_STORAGE.getValue()?.radius ?? null;
    LAST_LOCATION_STORAGE.setValue({
        query: location.query,
        lat: location.lat,
        lng: location.lng,
        radius: previousRadius,
    });
}

/**
 * Seed mode + location for an activity. Runs once per activity: re-entering the same PDP
 * keeps whatever the user picked, while navigating to a different one starts clean so the
 * previous page's venue can never leak into this activity's quote.
 */
export function seedWidgetStore(seed: {
    activityId: number;
    mode: WidgetMode;
    location: WidgetLocation | null;
}) {
    if (useLocationWidgetStore.getState().seededActivityId === seed.activityId) return;
    useLocationWidgetStore.setState({
        mode: seed.mode,
        location: seed.location,
        // Venue ids are activity-specific — never carry one across.
        fixedAddressId: null,
        seededActivityId: seed.activityId,
    });
}
